/**
 * La ventana que acompaña el alta de Stripe mientras ocurre en otra pestaña.
 *
 * Tiene dos caras y las decide el estado que llega del servidor, no un clic:
 *
 *   - Esperando. Un giro y una frase. Quien la ve está en la otra pestaña
 *     subiendo documentos, así que aquí no hay nada que pedirle.
 *   - Lista. Un visto verde en cuanto Stripe habilita los cobros —o dice que
 *     está revisando— y un botón para cerrar.
 *
 * El paso de una a otra no lo dispara nadie: quien sondea es la pantalla que la
 * abre, y esta ventana sólo pinta lo que le llega. Así el visto aparece aunque
 * el usuario no vuelva a mirar, que era justo lo que fallaba con la banda.
 */
import { useI18n } from '../../../lib/i18n'
import { useEffect } from 'react'
import { IconCheck, IconLoader2 } from '@tabler/icons-react'
import type { ConnectStatus } from '../../../lib/api'

export function StripeOnboardingDialog({ status, onClose }: {
  status: ConnectStatus
  onClose: () => void
}) {
  const { t } = useI18n()
  /* `restricted` cuenta como terminado: el cliente ya hizo su parte y lo que
     falta es que Stripe revise. Dejarlo girando diría que le queda trabajo. */
  const done = status === 'ready' || status === 'restricted'

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div className="dash-stripe-onboarding" role="presentation" onClick={onClose}>
      <div
        className="dash-stripe-onboarding-card"
        role="dialog"
        aria-modal="true"
        aria-label={done ? t('stripe.onboarding.doneAria') : t('stripe.onboarding.progressAria')}
        aria-live="polite"
        onClick={(event) => event.stopPropagation()}
      >
        <span className={`dash-stripe-onboarding-mark${done ? ' is-done' : ''}`} aria-hidden="true">
          {done ? <IconCheck size={30} /> : <IconLoader2 size={28} className="dash-spin" />}
        </span>

        {done ? (
          <>
            <h3>{status === 'ready' ? t('stripe.onboarding.readyTitle') : t('stripe.onboarding.reviewTitle')}</h3>
            <p>
              {status === 'ready' ? t('stripe.onboarding.readyBody') : t('stripe.onboarding.reviewBody')}
            </p>
            <button type="button" className="dash-primary-btn" onClick={onClose}>
              <IconCheck size={15} /> {t('stripe.onboarding.done')}
            </button>
          </>
        ) : (
          <>
            <h3>{t('stripe.onboarding.title')}</h3>
            <p>{t('stripe.onboarding.body')}</p>
            {/* Salida a mano, para quien cierra la otra pestaña a medias o
                decide dejarlo para luego. No cancela nada: el alta sigue donde
                la dejó y el botón de la ficha la retoma. */}
            <button type="button" className="dash-secondary-btn" onClick={onClose}>
              {t('stripe.onboarding.later')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
